import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AuthUserDto } from "@hisense/shared";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { canSwitchRegion, roleAccess } from "@hisense/shared";
import { bindViewRegionUser } from "./viewRegion";

interface AuthState {
  user: AuthUserDto | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Công ty đang xem (v1.8 mục 1.1) gắn theo người dùng TRƯỚC khi cây con thấy người dùng đó — để yêu cầu đầu tiên của mọi màn đã mang
 * đúng header. Đổi tài khoản là đọc lại lựa chọn của đúng người đó.
 */
function bindUser(u: AuthUserDto | null) {
  bindViewRegionUser(u?.id ?? null, !!u && canSwitchRegion(u.access ?? roleAccess(u.role)));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const cache=useQueryClient();
  const [user, setUserState] = useState<AuthUserDto | null>(null);
  const setUser = useCallback((u: AuthUserDto | null) => {
    bindUser(u);
    setUserState(u);
  }, []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    apiFetch<{ user: AuthUserDto }>("/auth/me")
      .then((r) => active && setUser(r.user))
      .catch(() => active && setUser(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  /**
   * `identifier` chứ không phải `email`: ô này nhận email nhân viên HOẶC mã
   * nhà cung cấp (khách chốt 15/09/2026). Tên tham số nói đúng thứ nó chứa.
   */
  const login = useCallback(async (identifier: string, password: string) => {
    const r = await apiFetch<{ user: AuthUserDto }>("/auth/login", {
      method: "POST",
      body: { identifier, password },
    });
    cache.clear();
    setUser(r.user);
  }, [cache]);

  const logout = useCallback(async () => {
    await apiFetch("/auth/logout", { method: "POST" }).catch(() => {});
    cache.clear();
    setUser(null);
  }, [cache]);
  const refresh=useCallback(async()=>{const r=await apiFetch<{user:AuthUserDto}>("/auth/me");cache.clear();setUser(r.user);},[cache]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
